import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { KineticHeading, Reveal } from "@/components/site/Reveal";
import EditorialMarquee from "@/components/site/EditorialMarquee";
import ProductCard from "@/components/site/ProductCard";

const HERO_IMG =
  "https://images.pexels.com/photos/10974994/pexels-photo-10974994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

const chapters = [
  {
    n: "01",
    title: "Sourced, not bought",
    body: "Every board begins as slow-grown timber, seasoned for months before it ever meets a blade. We choose grain the way a jeweller chooses stone.",
    img: "https://images.pexels.com/photos/37358115/pexels-photo-37358115.jpeg",
  },
  {
    n: "02",
    title: "Joined by hand",
    body: "Mortise and tenon, dovetail, dowel. No shortcuts, no hidden staples. Our joinery is designed to outlast the people who commission it.",
    img: "https://images.pexels.com/photos/13005858/pexels-photo-13005858.jpeg",
  },
  {
    n: "03",
    title: "Finished to be touched",
    body: "Hand-rubbed oils and waxes let the wood breathe and deepen with age. A Gulhar piece looks better in year ten than in year one.",
    img: "https://images.pexels.com/photos/945669/pexels-photo-945669.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.8]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="Gulhar craftsmanship" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-background" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-background/90 via-background/40 to-background/10" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-transparent to-background/30" />

        <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pb-24 md:pb-28">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="text-xs md:text-sm tracking-[0.4em] uppercase text-primary mb-6"
          >
            Est. Srinagar · Handcrafted Furniture
          </motion.p>
          <KineticHeading
            className="font-serif font-light leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-foreground"
            lines={["Furniture made", "to be inherited,"]}
          />
          <KineticHeading
            className="font-serif italic font-light leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-primary"
            lines={["not replaced."]}
            delay={0.24}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center"
          >
            <Link
              to="/collection"
              data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-3 border border-primary text-primary px-8 py-4 text-sm tracking-wide transition-colors duration-400 hover:bg-primary hover:text-primary-foreground"
            >
              Explore the Collection
              <ArrowRight size={16} className="transition-transform duration-400 group-hover:translate-x-1" />
            </Link>
            <span className="text-muted-foreground text-sm max-w-xs">A curated range of solid-wood pieces, built in our Kashmir workshop.</span>
          </motion.div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section data-testid="manifesto" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <Reveal>
          <p className="text-xs tracking-[0.4em] uppercase text-primary mb-4">The Gulhar Way</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl max-w-3xl leading-tight">
            Three convictions behind every piece we make.
          </h2>
        </Reveal>

        <div className="mt-20 space-y-24">
          {chapters.map((c, i) => (
            <div key={c.n} className={`grid md:grid-cols-12 gap-8 md:gap-16 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}>
              <Reveal className="md:col-span-7 [direction:ltr]">
                <div className="flex items-baseline gap-6 mb-6">
                  <span className="font-serif text-6xl md:text-7xl text-primary/80">{c.n}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <h3 className="font-serif text-3xl md:text-5xl font-light mb-5">{c.title}</h3>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-xl">{c.body}</p>
              </Reveal>
              <Reveal delay={0.15} className="md:col-span-5 [direction:ltr]">
                <div className="overflow-hidden border border-border aspect-[4/5]">
                  <img src={c.img} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <EditorialMarquee />

      {/* FEATURED */}
      <section data-testid="featured" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-xs tracking-[0.4em] uppercase text-primary mb-4">Featured Pieces</p>
              <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight">Recently from<br />the workshop</h2>
            </div>
            <Link to="/collection" data-testid="featured-view-all" className="inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-primary hover-underline">
              View full collection <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <Reveal>
          <div className="border border-border bg-card px-8 md:px-16 py-16 md:py-24 text-center">
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight max-w-3xl mx-auto">
              Commission a piece, or visit the <span className="italic text-primary">showroom.</span>
            </h2>
            <p className="text-muted-foreground mt-6 max-w-xl mx-auto">
              Tell us what you have in mind. Our makers will help you choose timber, dimensions and finish.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="mt-10 inline-flex items-center gap-3 bg-primary text-primary-foreground px-10 py-4 text-sm tracking-wide transition-opacity duration-300 hover:opacity-90"
            >
              Start an Enquiry <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
